import { AlertTriangle, CheckCircle2, FileImage, FileText, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { formatBytes } from '@/content/copy';
import type { AssetInspection, AssetRole } from '@/ports/editor';
import { Modal } from '../Modal';

interface AssetImportDialogProps {
  readonly role: AssetRole;
  readonly inspection: AssetInspection | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onConfirm: (page: number) => Promise<void>;
  readonly onRetry: () => void;
}

export function AssetImportDialog({ role, inspection, loading, error, onClose, onConfirm, onRetry }: AssetImportDialogProps) {
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const title = role === 'background' ? 'Subir fondo propio' : role === 'pdf-art' ? 'Subir PDF' : role === 'sticker-image' ? 'Subir imagen del sticker' : 'Subir logo o imagen';
  const confirm = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      await onConfirm(page);
      onClose();
    } catch (caught) {
      setConfirmError(caught instanceof Error ? caught.message : 'No se pudo agregar el archivo.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>Cancelar</button>
          <button className="primary-action" type="button" disabled={!inspection || loading || confirming || inspection.pageCount > 20} onClick={() => void confirm()}>
            {confirming ? <LoaderCircle className="spin" /> : inspection?.kind === 'pdf' ? <FileText /> : <FileImage />}
            Agregar a la mesa
          </button>
        </>
      }
    >
      {loading ? <div className="operation-state"><LoaderCircle className="spin" /><strong>Preparando el archivo...</strong><span>Una foto del celular puede tardar unos segundos.</span></div> : null}
      {error ? <div className="operation-state is-error" role="alert"><AlertTriangle /><strong>No se pudo abrir el archivo</strong><span>{error}</span><button type="button" onClick={onRetry}>Elegir otro archivo</button></div> : null}
      {inspection ? (
        <div className="asset-inspection">
          <div className="asset-file-row">
            {inspection.kind === 'pdf' ? <FileText /> : <FileImage />}
            <div><strong>{inspection.file.name}</strong><span>{formatBytes(inspection.sizeBytes)} · {inspection.mimeType}</span></div>
          </div>
          {inspection.kind === 'pdf' ? (
            <div className="field-control">
              <label htmlFor="pdf-page">Página del PDF</label>
              <select id="pdf-page" value={page} onChange={(event) => setPage(Number(event.target.value))}>
                {Array.from({ length: inspection.pageCount }, (_, index) => index + 1).map((value) => <option key={value} value={value}>Página {value}</option>)}
              </select>
              <small>Se conserva el archivo original. Página 1 queda seleccionada por defecto.</small>
              {inspection.pageCount > 20 ? <p className="inline-error">El PDF supera el límite de 20 páginas.</p> : null}
            </div>
          ) : null}
          <div className={`quality-status ${inspection.quality === 'warning' ? 'is-warning' : 'is-ready'}`}>
            {inspection.quality === 'warning' ? <AlertTriangle /> : <CheckCircle2 />}
            <span><strong>{inspection.quality === 'warning' ? 'Revisar calidad' : 'Archivo listo'}</strong><small>{inspection.qualityMessage}</small></span>
          </div>
          {confirmError ? <div className="operation-state is-error" role="alert"><span>{confirmError}</span><button type="button" onClick={onRetry}>Probar con otro archivo</button></div> : null}
        </div>
      ) : null}
    </Modal>
  );
}
